import { useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { MailIcon, SendIcon, UserIcon, MessageSquareIcon, AlertCircleIcon } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { config } from '../lib/config';
import { submitContactForm } from '@/server/functions/contact.functions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';

const pageUrl = `${config.appUrl}/contact`;
const title = 'Contact CoRATES - Questions, Feedback, and Support';
const description =
  'Get in touch with the CoRATES team about the appraisal platform, systematic review workflows, feature requests, or partnership opportunities.';

export const Route = createFileRoute('/contact')({
  headers: () => ({
    'Cache-Control': 'public, s-maxage=3600, stale-while-revalidate=86400',
  }),
  head: () => ({
    meta: [
      { title },
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: pageUrl },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ],
    links: [{ rel: 'canonical', href: pageUrl }],
  }),
  component: ContactPage,
});

type FormState = 'idle' | 'sending' | 'sent' | 'error';

function ContactPage() {
  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const isSubmittingRef = useRef(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setFormState('sending');
    setErrorMessage('');

    // React nullifies currentTarget after this handler yields at await.
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      subject: formData.get('subject'),
      message: formData.get('message'),
    };

    try {
      await submitContactForm({
        data: {
          name: data.name as string,
          email: data.email as string,
          subject: (data.subject as string) || undefined,
          message: data.message as string,
        },
      });

      setFormState('sent');
      form.reset();
    } catch (err) {
      console.error('Contact form error:', err);
      setErrorMessage(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      );
      setFormState('error');
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <div className='min-h-screen bg-linear-to-b from-blue-50/30 to-white'>
      <Navbar />
      <main className='flex-1 py-12'>
        <div className='mx-auto max-w-6xl px-6'>
          <div className='mb-16 text-center'>
            <h1 className='mb-4 text-4xl font-bold text-gray-900 sm:text-5xl'>Get in Touch</h1>
            <p className='mx-auto max-w-2xl text-xl text-gray-600'>
              Have questions about CoRATES? We would love to hear from you. Send us a message and we
              will respond as soon as possible.
            </p>
          </div>

          <div className='grid gap-12 lg:grid-cols-2 lg:gap-16'>
            <div className='flex flex-col gap-8'>
              <div>
                <h2 className='mb-6 text-2xl font-semibold text-gray-900'>We are here to help</h2>
                <p className='mb-8 text-gray-600'>
                  Whether you have questions about our platform, need technical support, want to
                  provide feedback, or discuss partnership opportunities, our team is ready to
                  assist you. If you would like early access to the platform, please fill out the
                  form below with 'Early Access Request' as the subject.
                </p>
              </div>

              <div className='flex flex-col gap-6'>
                <div className='flex items-start gap-4'>
                  <div className='flex size-12 shrink-0 items-center justify-center rounded-lg bg-blue-100'>
                    <MailIcon className='size-6 text-blue-600' />
                  </div>
                  <div>
                    <h3 className='mb-1 font-semibold text-gray-900'>Send Us a Message</h3>
                    <p className='mt-1 text-gray-500'>We try to respond within 24 hours</p>
                  </div>
                </div>

                <div className='flex items-start gap-4'>
                  <div className='flex size-12 shrink-0 items-center justify-center rounded-lg bg-blue-100'>
                    <MessageSquareIcon className='size-6 text-blue-600' />
                  </div>
                  <div>
                    <h3 className='mb-1 font-semibold text-gray-900'>Feedback Welcome</h3>
                    <p className='text-gray-500'>
                      Your feedback helps us improve CoRATES for everyone.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className='rounded-2xl border border-gray-100 bg-white p-8 shadow-lg'>
              <h2 className='mb-6 text-xl font-semibold text-gray-900'>Send us a message</h2>
              <form onSubmit={handleSubmit} className='flex flex-col gap-6'>
                <div>
                  <Label htmlFor='name' className='mb-2'>
                    Your Name
                  </Label>
                  <div className='relative'>
                    <UserIcon className='absolute top-1/2 left-3 size-5 -translate-y-1/2 text-gray-400' />
                    <Input
                      type='text'
                      id='name'
                      name='name'
                      required
                      className='h-auto py-3 pr-4 pl-10 md:text-base'
                      placeholder='John Doe'
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor='email' className='mb-2'>
                    Email Address
                  </Label>
                  <div className='relative'>
                    <MailIcon className='absolute top-1/2 left-3 size-5 -translate-y-1/2 text-gray-400' />
                    <Input
                      type='email'
                      id='email'
                      name='email'
                      required
                      className='h-auto py-3 pr-4 pl-10 md:text-base'
                      placeholder='you@example.com'
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor='subject' className='mb-2'>
                    Subject
                  </Label>
                  <Input
                    type='text'
                    id='subject'
                    name='subject'
                    className='h-auto px-4 py-3 md:text-base'
                    placeholder='What is this about?'
                  />
                </div>

                <div>
                  <Label htmlFor='message' className='mb-2'>
                    Message
                  </Label>
                  <Textarea
                    id='message'
                    name='message'
                    required
                    rows={5}
                    className='resize-none px-4 py-3 md:text-base'
                    placeholder='Tell us how we can help...'
                  />
                </div>

                <Button
                  type='submit'
                  size='lg'
                  disabled={formState === 'sending'}
                  className='h-auto w-full gap-2 rounded-lg py-3 text-base'
                >
                  {formState === 'sending' ?
                    <>
                      <Spinner size='sm' variant='current' className='size-5' />
                      Sending...
                    </>
                  : <>
                      <SendIcon className='size-5' />
                      Send Message
                    </>
                  }
                </Button>

                {formState === 'sent' && (
                  <p className='text-center text-sm text-green-600'>
                    Your message has been sent successfully. We will get back to you soon!
                  </p>
                )}

                {formState === 'error' && (
                  <div className='flex items-center justify-center gap-2 text-sm text-red-600'>
                    <AlertCircleIcon className='size-4' />
                    <p>{errorMessage}</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
